/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { assistantApi } from '../../services/api';
import type { ChatSession, ChatMessage, SendMessageRequest } from '../../types';

interface AssistantState {
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  isSendingMessage: boolean;
}

const initialState: AssistantState = {
  sessions: [],
  currentSession: null,
  messages: [],
  isLoading: false,
  error: null,
  isSendingMessage: false,
};

// Async thunks
export const sendMessage = createAsyncThunk(
  'assistant/sendMessage',
  async (data: SendMessageRequest, { rejectWithValue }) => {
    try {
      const response = await assistantApi.sendMessage(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to send message');
    }
  }
);

export const getChatSessions = createAsyncThunk(
  'assistant/getChatSessions',
  async (_, { rejectWithValue }) => {
    try {
      const sessions = await assistantApi.getChatSessions();
      return sessions;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get chat sessions');
    }
  }
);

export const getChatSession = createAsyncThunk(
  'assistant/getChatSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const session = await assistantApi.getChatSession(sessionId);
      return session;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get chat session');
    }
  }
);

export const deleteChatSession = createAsyncThunk(
  'assistant/deleteChatSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      await assistantApi.deleteChatSession(sessionId);
      return sessionId;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to delete chat session');
    }
  }
);

export const getNutritionAnalysis = createAsyncThunk(
  'assistant/getNutritionAnalysis',
  async (foodName: string, { rejectWithValue }) => {
    try {
      const analysis = await assistantApi.getNutritionAnalysis(foodName);
      return analysis;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get nutrition analysis');
    }
  }
);

export const getMealSuggestions = createAsyncThunk(
  'assistant/getMealSuggestions',
  async (preferences: string[] | undefined, { rejectWithValue }) => {
    try {
      const suggestions = await assistantApi.getMealSuggestions(preferences);
      return suggestions;
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to get meal suggestions');
    }
  }
);

// Slice
const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentSession: (state) => {
      state.currentSession = null;
      state.messages = [];
    },
    addUserMessage: (state, action) => {
      const message: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: action.payload,
        timestamp: new Date().toISOString(),
      };
      state.messages.push(message);
    },
  },
  extraReducers: (builder) => {
    // Send message
    builder.addCase(sendMessage.pending, (state) => {
      state.isSendingMessage = true;
      state.error = null;
    });
    builder.addCase(sendMessage.fulfilled, (state, action) => {
      state.isSendingMessage = false;

      // Add assistant's response to messages
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: action.payload.message,
        timestamp: new Date().toISOString(),
      };
      state.messages.push(assistantMessage);

      // Update current session ID if this is a new session
      if (!state.currentSession) {
        state.currentSession = {
          id: action.payload.session_id,
          user_id: '',
          messages: state.messages,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }

      state.error = null;
    });
    builder.addCase(sendMessage.rejected, (state, action) => {
      state.isSendingMessage = false;
      state.error = action.payload as string;
    });

    // Get chat sessions
    builder.addCase(getChatSessions.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getChatSessions.fulfilled, (state, action) => {
      state.isLoading = false;
      state.sessions = action.payload;
      state.error = null;
    });
    builder.addCase(getChatSessions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get chat session
    builder.addCase(getChatSession.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getChatSession.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentSession = action.payload;
      state.messages = action.payload.messages;
      state.error = null;
    });
    builder.addCase(getChatSession.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete chat session
    builder.addCase(deleteChatSession.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteChatSession.fulfilled, (state, action) => {
      state.isLoading = false;
      state.sessions = state.sessions.filter((s) => s.id !== action.payload);
      if (state.currentSession?.id === action.payload) {
        state.currentSession = null;
        state.messages = [];
      }
      state.error = null;
    });
    builder.addCase(deleteChatSession.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get nutrition analysis
    builder.addCase(getNutritionAnalysis.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getNutritionAnalysis.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(getNutritionAnalysis.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Get meal suggestions
    builder.addCase(getMealSuggestions.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getMealSuggestions.fulfilled, (state) => {
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(getMealSuggestions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, clearCurrentSession, addUserMessage } = assistantSlice.actions;
export default assistantSlice.reducer;
